import React, { useState, useEffect } from 'react';
import { Sparkles, Send, Loader2, CheckCircle2, XCircle, Trash2, ArrowRightLeft, TrendingUp, TrendingDown, Wallet, Image as ImageIcon, Camera, X, WifiOff } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { parseNaturalLanguageTransaction, ParsedTransaction, AIServiceError, FallbackRequest } from '../services/aiService';
import { createTransaction, createTransfer } from '../services/financeService';
import { AIFallbackModal } from './AIFallbackModal';
import { Account, Transaction } from '../types';
import { useFirebase } from './FirebaseProvider';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { VoiceInput } from './VoiceInput';
import { getInstitutionById } from '../constants/institutions';
import { requestNotificationPermission, sendAppNotification } from '../utils/notifications';

interface AITransactionInputProps {
  accounts: Account[];
  transactions: Transaction[];
  onSuccess: () => void;
}

export const AITransactionInput: React.FC<AITransactionInputProps> = ({ accounts, transactions, onSuccess }) => {
  const { user, effectiveUserId } = useFirebase();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<ParsedTransaction[]>([]);
  const [selectedImage, setSelectedImage] = useState<{ data: string; mimeType: string } | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const [fallbackRequest, setFallbackRequest] = useState<FallbackRequest | null>(null);
  const fallbackResolver = React.useRef<((confirmed: boolean) => void) | null>(null);

  const handleTranscription = (text: string) => {
    setInput(prev => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n- ${text}` : `- ${text}`;
    });
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const formatPreviewDate = (dateStr?: string) => {
    try {
      if (!dateStr) return format(new Date(), "d 'de' MMMM, yyyy", { locale: es });
      
      // Normalize date string to avoid timezone shifts if it's just YYYY-MM-DD
      const normalizedDateStr = dateStr.length === 10 ? `${dateStr}T12:00:00` : dateStr;
      const d = new Date(normalizedDateStr);
      
      if (isNaN(d.getTime())) return format(new Date(), "d 'de' MMMM, yyyy", { locale: es });
      return format(d, "d 'de' MMMM, yyyy", { locale: es });
    } catch (e) {
      return format(new Date(), "d 'de' MMMM, yyyy", { locale: es });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file);
  };

  const processImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setStatus('error');
      setMessage('Por favor selecciona un archivo de imagen válido.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = (reader.result as string).split(',')[1];
      setSelectedImage({
        data: base64String,
        mimeType: file.type
      });
      setImagePreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault(); // Prevent pasting the image as text/blob in the textarea
          processImageFile(file);
          break; // Only process the first image found
        }
      }
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
  };

  const handleParse = async (e?: React.FormEvent | React.KeyboardEvent) => {
    if (e) e.preventDefault();
    if ((!input.trim() && !selectedImage) || !user) return;

    setLoading(true);
    setStatus('idle');
    setMessage('');
    setPreview([]); // Clear previous preview when starting a new parse

    try {
      // Pedimos permiso de notificación silenciosamente previo a iniciar el parse
      requestNotificationPermission();

      const onFallback = (req: FallbackRequest): Promise<boolean> => {
        setFallbackRequest(req);
        return new Promise((resolve) => {
          fallbackResolver.current = resolve;
        });
      };

      const parsed = await parseNaturalLanguageTransaction(
        effectiveUserId!, 
        input, 
        accounts, 
        selectedImage || undefined,
        onFallback
      );
      
      if (parsed.length === 0) {
        throw new Error("No pudimos entender las transacciones. Intenta ser más específico o subir una imagen más clara.");
      }

      // Validar si la IA no pudo determinar alguna cuenta
      const hasUnknownAccounts = parsed.some(tx => tx.details.some(d => d.accountId === 'UNKNOWN'));
      if (hasUnknownAccounts) {
        throw new Error("No fue posible determinar con seguridad a qué cuenta aplicar este registro. Por favor, menciona explícitamente el nombre de la cuenta (ej. 'Itaú', 'Ueno' o 'Efectivo').");
      }

      sendAppNotification('Entrada Inteligente Completada', {
        body: 'El asistente ha procesado tu documento correctamente. Revisa y confirma.',
        icon: '/favicon.ico' // Usa el icono de la app si hay
      });

      setPreview(parsed);
    } catch (err: any) {
      console.error("AI Parse Error:", err);
      setStatus('error');
      
      let errorMessage = "Ocurrió un error al procesar tu solicitud.";
      if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      sendAppNotification('Fallo de Entrada Inteligente', {
        body: errorMessage,
      });

      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!effectiveUserId || preview.length === 0) return;
    setRegistering(true);

    try {
      for (const parsed of preview) {
        const mappedDetails = parsed.details.map(detail => {
          let account = accounts.find(a => a.id === detail.accountId);
          
          // Fallback just in case the AI returns a name instead of ID
          if (!account) {
            account = accounts.find(a => {
              const instName = a.institutionId ? getInstitutionById(a.institutionId)?.name : '';
              const fullName = instName ? `${a.name} (${instName})` : a.name;
              const searchStr = (detail.accountId || '').toLowerCase();
              return a.id === searchStr || 
                     a.name.toLowerCase() === searchStr ||
                     fullName.toLowerCase() === searchStr ||
                     a.name.toLowerCase().includes(searchStr) ||
                     searchStr.includes(a.name.toLowerCase());
            });
          }
          
          if (!account) {
            throw new Error(`No encontramos la cuenta especificada por la IA.`);
          }
          
          return {
            accountId: account.id,
            amount: detail.amount,
            type: detail.type as 'CREDIT' | 'DEBIT'
          };
        });

        if (parsed.type === 'TRANSFER') {
          const from = mappedDetails.find(d => d.type === 'DEBIT');
          const to = mappedDetails.find(d => d.type === 'CREDIT');
          
          if (from && to) {
            let txDate = new Date().toISOString();
            try {
              if (parsed.date) {
                // If it's just a YYYY-MM-DD string, append T12:00:00 to avoid timezone shifts
                const dateStr = parsed.date.length === 10 ? `${parsed.date}T12:00:00` : parsed.date;
                const d = new Date(dateStr);
                if (!isNaN(d.getTime())) {
                  txDate = d.toISOString();
                }
              }
            } catch (e) {
              console.warn("Invalid date from AI, using current date");
            }

            await createTransfer(effectiveUserId, {
              description: parsed.description || 'Transferencia IA',
              date: txDate,
              fromAccountId: from.accountId,
              toAccountId: to.accountId,
              amount: from.amount
            });
          }
        } else {
          let txDate = new Date().toISOString();
          try {
            if (parsed.date) {
              // If it's just a YYYY-MM-DD string, append T12:00:00 to avoid timezone shifts
              const dateStr = parsed.date.length === 10 ? `${parsed.date}T12:00:00` : parsed.date;
              const d = new Date(dateStr);
              if (!isNaN(d.getTime())) {
                txDate = d.toISOString();
              }
            }
          } catch (e) {
            console.warn("Invalid date from AI, using current date");
          }

          await createTransaction(effectiveUserId, {
            description: parsed.description,
            type: parsed.type,
            date: txDate,
            details: mappedDetails
          });
        }
      }

      setStatus('success');
      setMessage(`¡${preview.length} transacciones registradas con éxito!`);
      setPreview([]);
      setInput('');
      removeImage();
      setTimeout(() => {
        setStatus('idle');
        onSuccess();
      }, 3000);

    } catch (err: any) {
      console.error("AI Registration Error:", err);
      setStatus('error');
      setMessage(err.message || "Error al registrar las transacciones.");
    } finally {
      setRegistering(false);
    }
  };

  const removePreviewItem = (index: number) => {
    setPreview(prev => prev.filter((_, i) => i !== index));
  };

  const getAccountDisplayName = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account) return 'Cuenta Desconocida';
    const instName = account.institutionId ? getInstitutionById(account.institutionId)?.name : '';
    return instName ? `${account.name} - ${instName}` : account.name;
  };

  // Totals calculation
  const totalGeneral = preview.reduce((sum, tx) => {
    if (tx.type === 'TRANSFER') return sum;
    const txTotal = tx.details.reduce((s, d) => s + (d.type === 'CREDIT' ? d.amount : -d.amount), 0);
    return sum + txTotal;
  }, 0);

  const accountTotals = preview.reduce((acc, tx) => {
    tx.details.forEach(d => {
      const accountName = getAccountDisplayName(d.accountId);
      const current = acc[accountName] || 0;
      acc[accountName] = current + (d.type === 'CREDIT' ? d.amount : -d.amount);
    });
    return acc;
  }, {} as Record<string, number>);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!loading && !registering && isOnline && (input.trim() || selectedImage)) {
        handleParse(e);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
        {!isOnline && (
          <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center text-center p-6">
            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-4 border border-amber-500/20">
              <WifiOff className="text-amber-500" size={32} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Sin Conexión</h3>
            <p className="text-sm text-zinc-400 max-w-xs">
              La entrada inteligente requiere conexión a internet para procesar el texto o imagen. Puedes registrar operaciones manualmente usando los botones de arriba.
            </p>
          </div>
        )}
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
            <Sparkles className="text-indigo-400" size={18} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-zinc-100">Entrada Inteligente (Múltiple)</h3>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Describe varios movimientos a la vez</p>
          </div>
        </div>

        <form onSubmit={handleParse} className="relative space-y-3">
          <div className="relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              placeholder="Ej: Gasté 50000 en combustible con Tarjeta, o pega/sube una foto de tu comprobante (Presiona Ctrl+Enter para enviar)"
              disabled={loading || registering || !isOnline}
              rows={3}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-4 pr-12 py-3 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all placeholder:text-zinc-600 resize-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={loading || registering || (!input.trim() && !selectedImage) || !isOnline}
              className="absolute right-3 bottom-3 p-2 bg-indigo-500 hover:bg-indigo-600 text-black rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <label className={`flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors text-xs font-medium border border-zinc-700 ${!isOnline ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
              <ImageIcon size={16} className="text-indigo-400" />
              <span className="hidden sm:inline">Subir Imagen</span>
              <span className="sm:hidden">Subir</span>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleImageChange} 
                className="hidden" 
                disabled={loading || registering || !isOnline}
              />
            </label>

            <label className={`flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors text-xs font-medium border border-zinc-700 ${!isOnline ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
              <Camera size={16} className="text-indigo-400" />
              <span className="hidden sm:inline">Tomar Foto</span>
              <span className="sm:hidden">Cámara</span>
              <input 
                type="file" 
                accept="image/*" 
                capture="environment"
                onChange={handleImageChange} 
                className="hidden" 
                disabled={loading || registering || !isOnline}
              />
            </label>

            <VoiceInput 
              onTranscription={handleTranscription} 
              isOnline={isOnline} 
              accounts={accounts}
              recentTransactions={transactions.slice(0, 10)}
            />
            
            <AnimatePresence>
              {imagePreviewUrl && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="relative group"
                >
                  <img 
                    src={imagePreviewUrl} 
                    alt="Preview" 
                    className="w-10 h-10 rounded-lg object-cover border border-indigo-500/50"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={10} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </form>

        <AnimatePresence>
          {status !== 'idle' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className={`mt-4 p-3 rounded-xl border flex items-center gap-3 text-xs font-medium ${
                status === 'success' 
                  ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
                  : 'bg-red-500/10 border-red-500/20 text-red-400'
              }`}
            >
              {status === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              {message}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {preview.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl"
          >
            <div className="p-4 border-b border-zinc-800 bg-zinc-800/30 flex items-center justify-between">
              <h4 className="text-sm font-bold flex items-center gap-2">
                <Sparkles size={16} className="text-indigo-400" />
                Vista Previa de Registros ({preview.length})
              </h4>
              <button 
                onClick={() => setPreview([])}
                className="text-xs text-zinc-500 hover:text-white transition-colors"
              >
                Cancelar
              </button>
            </div>

            <div className="divide-y divide-zinc-800 max-h-[400px] overflow-y-auto custom-scrollbar">
              {preview.map((tx, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${
                      tx.type === 'INCOME' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' :
                      tx.type === 'EXPENSE' ? 'bg-red-500/10 border-red-500/20 text-red-500' :
                      'bg-blue-500/10 border-blue-500/20 text-blue-500'
                    }`}>
                      {tx.type === 'INCOME' ? <TrendingUp size={14} /> :
                       tx.type === 'EXPENSE' ? <TrendingDown size={14} /> :
                       <ArrowRightLeft size={14} />}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{tx.description}</div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                        <span className="font-medium text-zinc-400">{formatPreviewDate(tx.date)}</span>
                        <span>•</span>
                        <span>{tx.details.map(d => getAccountDisplayName(d.accountId)).join(' ➔ ')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className={`text-sm font-bold ${
                        tx.type === 'INCOME' ? 'text-emerald-500' :
                        tx.type === 'EXPENSE' ? 'text-red-500' :
                        'text-zinc-100'
                      }`}>
                        {formatCurrency(tx.details[0].amount)}
                      </div>
                      <div className="text-[9px] text-zinc-600 uppercase font-bold">{tx.type}</div>
                    </div>
                    <button 
                      onClick={() => removePreviewItem(idx)}
                      className="p-2 text-zinc-700 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals Section */}
            <div className="p-4 bg-zinc-950/50 border-t border-zinc-800 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest">Impacto por Cuenta</div>
                  <div className="space-y-1">
                    {(Object.entries(accountTotals) as [string, number][]).map(([name, total]) => (
                      <div key={name} className="flex justify-between text-[11px]">
                        <span className="text-zinc-400">{name}</span>
                        <span className={total >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                          {total > 0 ? '+' : ''}{formatCurrency(total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-right flex flex-col justify-end">
                  <div className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest">Balance Neto</div>
                  <div className={`text-xl font-bold ${totalGeneral >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {totalGeneral > 0 ? '+' : ''}{formatCurrency(totalGeneral)}
                  </div>
                </div>
              </div>

              <button
                onClick={handleConfirm}
                disabled={registering}
                className="w-full py-3 bg-indigo-500 hover:bg-indigo-600 text-black font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20"
              >
                {registering ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <CheckCircle2 size={20} />
                    Confirmar y Registrar Todo
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {fallbackRequest && (
          <AIFallbackModal
            request={fallbackRequest}
            onConfirm={() => {
              fallbackResolver.current?.(true);
              setFallbackRequest(null);
            }}
            onCancel={() => {
              fallbackResolver.current?.(false);
              setFallbackRequest(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
